import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    FolderOpen,
    FileText,
    Upload,
    Download,
    Search,
    Filter,
    Grid,
    List,
    MoreVertical,
    Star,
    Trash2,
    Share2,
    Eye,
    Calendar,
    User,
    HardDrive,
    Cloud,
    Shield,
    Plus,
    ChevronRight,
    Home,
    Settings,
    LogOut,
    Bell,
    Menu,
    X,
    RefreshCw,
    FolderPlus,
    AlertCircle,
    Edit,
    Move,
    Copy,
    CheckCircle,
    XCircle,
    Folder,
    CheckSquare,
    Square,
    Minus,
    RotateCcw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotify } from '../context/NotificationContext';
import '../styles/Dashboard.css';
import { FilterPanel } from '../components/FilterPanel';
import { CreateFolderModal } from '../components/CreateFolderModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShareModal } from '../components/ShareModal';
import { RenameModal } from '../components/RenameModal';
import { MoveModal } from '../components/MoveModal';
import FileViewer from '../components/FileViewer';
import '../styles/FileViewer.css';
import logo from '../timefiles.png'
import { FiRefreshCcw, FiUpload } from 'react-icons/fi';
import NotificationInbox from '../components/NotificationInbox';
import NotificationInboxFull from '../components/NotificationInboxFull';


const Dashboard = () => {
    const { user, logout, FILE_APP_URL, getCookie } = useAuth();
    const { success, error, spinner, choice } = useNotify();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Get initial state from URL params
    const urlFolder = searchParams.get('folder');
    const urlView = searchParams.get('view');
    const validViews = ['home', 'starred', 'shared', 'trash'];

    // UI State
    const [viewMode, setViewMode] = useState(localStorage.getItem('viewMode'));
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItems, setSelectedItems] = useState([]); // Changed from selectedFiles
    const [selectionMode, setSelectionMode] = useState(false);
    const [showBatchActions, setShowBatchActions] = useState(false);
    const [currentFolder, setCurrentFolder] = useState(urlFolder || null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activeView, setActiveView] = useState(validViews.includes(urlView) ? urlView : 'home');
    const [dragOver, setDragOver] = useState(false);
    const [showFileViewer, setShowFileViewer] = useState(false);
    const [currentViewerFile, setCurrentViewerFile] = useState(null);
    const [viewerFileIndex, setViewerFileIndex] = useState(0);
    const [folderValidated, setFolderValidated] = useState(!urlFolder); // Track if URL folder has been validated

    // Data State
    const [files, setFiles] = useState([]);
    const [folders, setFolders] = useState([]);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [storageStats, setStorageStats] = useState({
        used: 0,
        total: 100,
        percentage: 0
    });
    const [stats, setStats] = useState({
        fileCount: 0,
        folderCount: 0,
        totalSize: 0
    });
    const [uploadConfig, setUploadConfig] = useState({
        max_file_size: 100 * 1024 * 1024,
        allowed_extensions: []
    });

    // Modals
    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showFullInbox, setShowFullInbox] = useState(false);
    const [existingShare, setExistingShare] = useState(null);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [contextMenu, setContextMenu] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [sidebarOpenBeforeViewer, setSidebarOpenBeforeViewer] = useState(false);
    const [sharedStats, setSharedStats] = useState({
        sharedFileCount: 0,
        sharedFolderCount: 0
    });

    // Trash stats
    const [trashStats, setTrashStats] = useState({
        deleted_files_count: 0,
        deleted_folders_count: 0,
        total_size_bytes: 0,
        total_size_formatted: '0 B'
    });

    // Upload state
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState([]);

    // File upload
    const fileInputRef = useRef(null);

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        fileType: '',
        dateRange: '',
        starred: false
    });

    // Batch operation functions
    const toggleSelectionMode = () => {
        setSelectionMode(!selectionMode);
        setSelectedItems([]);
        setShowBatchActions(false);
    };

    const toggleItemSelection = (item) => {
        setSelectedItems(prev => {
            const isSelected = prev.some(selected => selected.id === item.id && selected.type === item.type);
            if (isSelected) {
                return prev.filter(selected => !(selected.id === item.id && selected.type === item.type));
            } else {
                return [...prev, { ...item, type: item.type }];
            }
        });
    };

    const selectAll = () => {
        if (selectedItems.length === allItems.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems([...allItems]);
        }
    };

    const getSelectionStatus = () => {
        if (selectedItems.length === 0) return 'none';
        if (selectedItems.length === allItems.length) return 'all';
        return 'partial';
    };

    const batchDelete = async () => {
        if (selectedItems.length === 0) return;

        const itemNames = selectedItems.map(item => item.name).join(', ');
        choice(`Are you sure you want to delete ${selectedItems.length} item(s)?\n\n${itemNames}`, async () => {
            setLoading(true);
            const results = [];

            try {
                for (const item of selectedItems) {
                    const isFolder = item.type === 'folder';
                    const endpoint = isFolder ? `/folders/${item.id}` : `/files/${item.id}`;

                    try {
                        await fileApiRequest(endpoint, {
                            method: 'DELETE',
                            headers: {
                                'X-CSRF-TOKEN': getCookie('csrf_access_token')
                            }
                        });
                        results.push({ item: item.name, success: true });
                    } catch (err) {
                        results.push({ item: item.name, success: false, error: err.message });
                    }
                }

                const successful = results.filter(r => r.success).length;
                const failed = results.filter(r => !r.success).length;

                if (successful > 0) {
                    success(`${successful} item(s) deleted successfully!`);
                }
                if (failed > 0) {
                    const failedItems = results.filter(r => !r.success).map(r => r.item).join(', ');
                    error(`Failed to delete: ${failedItems}`);
                }

                setSelectedItems([]);
                setSelectionMode(false);
                setShowBatchActions(false);
                loadData();
                loadTrashStats();

            } catch (err) {
                error(`Batch delete error: ${err.message}`);
            } finally {
                setLoading(false);
            }
        });
    };

    const batchRestore = async () => {
        if (selectedItems.length === 0) return;

        const deletedItems = selectedItems.filter(item => item.is_deleted);
        if (deletedItems.length === 0) {
            error('No deleted items selected for restoration');
            return;
        }

        const itemNames = deletedItems.map(item => item.name).join(', ');
        choice(`Are you sure you want to restore ${deletedItems.length} item(s)?\n\n${itemNames}`, async () => {
            setLoading(true);
            const results = [];

            try {
                for (const item of deletedItems) {
                    const isFolder = item.type === 'folder';
                    const endpoint = isFolder ? `/folders/${item.id}` : `/files/${item.id}`;

                    try {
                        await fileApiRequest(endpoint, {
                            method: 'PATCH',
                            headers: {
                                'content-type': 'application/json',
                                'X-CSRF-TOKEN': getCookie('csrf_access_token')
                            },
                            body: JSON.stringify({
                                'action': 'restore'
                            })
                        });
                        results.push({ item: item.name, success: true });
                    } catch (err) {
                        results.push({ item: item.name, success: false, error: err.message });
                    }
                }

                const successful = results.filter(r => r.success).length;
                const failed = results.filter(r => !r.success).length;

                if (successful > 0) {
                    success(`${successful} item(s) restored successfully!`);
                }
                if (failed > 0) {
                    const failedItems = results.filter(r => !r.success).map(r => r.item).join(', ');
                    error(`Failed to restore: ${failedItems}`);
                }

                setSelectedItems([]);
                setSelectionMode(false);
                setShowBatchActions(false);
                loadData();
                loadTrashStats();

            } catch (err) {
                error(`Batch restore error: ${err.message}`);
            } finally {
                setLoading(false);
            }
        });
    };

    // Update useEffect to show/hide batch actions
    useEffect(() => {
        setShowBatchActions(selectedItems.length > 0);
    }, [selectedItems]);

    // Updated File Management API Helper
    const fileApiRequest = async (endpoint, options = {}) => {
        try {
            const defaultHeaders = {};

            if (options.body && !(options.body instanceof FormData)) {
                defaultHeaders['Content-Type'] = 'application/json';
            }

            // Add CSRF token for mutation methods
            const method = (options.method || 'GET').toUpperCase();
            if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
                defaultHeaders['X-CSRF-TOKEN'] = getCookie('csrf_access_token');
            }

            const response = await fetch(`${FILE_APP_URL}${endpoint}`, {
                credentials: 'include',
                headers: {
                    ...defaultHeaders,
                    ...options.headers
                },
                ...options
            });

            if (!response.ok) {
                error(response);
                return;
            }

            return await response.json();
        } catch (err) {
            console.error('File API Error:', err);
            throw err;
        }
    };

    const viewFile = (file, index = 0) => {
        setCurrentViewerFile(file);
        setViewerFileIndex(index);
        setShowFileViewer(true);
    };

    const loadSharedStats = async () => {
        try {
            const [sharedFilesResp, sharedFoldersResp] = await Promise.all([
                fileApiRequest('/shared/files?per_page=1'),
                fileApiRequest('/shared/folders')
            ]);

            setSharedStats({
                sharedFileCount: sharedFilesResp.total || 0,
                sharedFolderCount: sharedFoldersResp.folders?.length || 0
            });
        } catch (err) {
            console.error('Failed to load shared stats:', err);
        }
    };

    const navigateViewerFile = (direction) => {
        const currentIndex = viewerFileIndex;
        const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

        if (newIndex >= 0 && newIndex < files.length) {
            setCurrentViewerFile(files[newIndex]);
            setViewerFileIndex(newIndex);
        }
    };

    useEffect(() => {
        if (showFileViewer) {
            setSidebarOpenBeforeViewer(sidebarOpen);
            setSidebarOpen(false);
        } else if (!showFileViewer && sidebarOpenBeforeViewer) {
            if (window.innerWidth >= 1024) {
                setSidebarOpen(true);
            }
            setSidebarOpenBeforeViewer(false);
        }
    }, [showFileViewer, sidebarOpen]);

    useEffect(() => {
        localStorage.setItem('viewMode', viewMode);
    }, [viewMode]);

    const handleViewerDownload = (file) => {
        downloadFile(file);
    };

    const handleViewerShare = (file) => {
        shareItem(file, false);
    };

    const handleViewerStar = (file) => {
        toggleStar(file, false);
    };

    const loadUploadConfig = async () => {
        try {
            const config = await fileApiRequest('/upload-config');
            setUploadConfig(config);
        } catch (err) {
            console.error('Failed to load upload config:', err);
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const searchParam = filters.search || searchTerm;
            const starredParam = activeView === 'starred' || filters.starred;
            const deletedParam = activeView === 'trash' || filters.starred;

            let filesResponse, foldersResponse;

            if (activeView === 'shared') {
                const [sharedFilesResp, sharedFoldersResp] = await Promise.all([
                    fileApiRequest(`/shared/files?folder_id=${currentFolder || ''}&starred=${starredParam}&search=${searchParam}`),
                    fileApiRequest(`/shared/folders?parent_folder_id=${currentFolder || ''}&search=${searchParam}`)
                ]);

                filesResponse = sharedFilesResp;
                foldersResponse = sharedFoldersResp;
            } else if (activeView === 'starred') {
                const [regularFilesResp, regularFoldersResp] = await Promise.all([
                    fileApiRequest(`/files?folder_id=${currentFolder || ''}&starred=${starredParam}&search=${searchParam}`),
                    fileApiRequest(`/folders?parent_folder_id=${currentFolder || ''}&starred=${starredParam}`)
                ]);

                filesResponse = regularFilesResp;
                foldersResponse = regularFoldersResp;
            } else {
                const [regularFilesResp, regularFoldersResp] = await Promise.all([
                    fileApiRequest(`/files?folder_id=${currentFolder || ''}&is_deleted=${deletedParam}&search=${searchParam}`),
                    fileApiRequest(`/folders?parent_folder_id=${currentFolder || ''}&is_deleted=${deletedParam}`)
                ]);

                filesResponse = regularFilesResp;
                foldersResponse = regularFoldersResp;
            }

            const safeFiles = (filesResponse?.files || []).filter(file => file && typeof file === 'object' && file.id);
            const safeFolders = (foldersResponse?.folders || []).filter(folder => folder && typeof folder === 'object' && folder.id);

            setFiles(safeFiles);
            setFolders(safeFolders);

            const fileCount = safeFiles.length;
            const folderCount = safeFolders.length;
            const totalSize = safeFiles.reduce((sum, file) => sum + (file.file_size || 0), 0);

            setStats({ fileCount, folderCount, totalSize });

            const storageUsed = totalSize / (1024 * 1024 * 1024);
            setStorageStats({
                used: storageUsed.toFixed(2),
                total: 100,
                percentage: Math.min((storageUsed / 100) * 100, 100)
            });

        } catch (err) {
            console.error('Load data error:', err);
            error(`Failed to load data: ${err.message}`);

            setFiles([]);
            setFolders([]);
            setStats({ fileCount: 0, folderCount: 0, totalSize: 0 });
        } finally {
            setLoading(false);
        }
    };

    const loadBreadcrumbs = async () => {
        if (!currentFolder) {
            setBreadcrumbs([]);
            return;
        }

        try {
            const response = await fileApiRequest(`/folders/${currentFolder}`);
            const folder = response.folder || response;

            if (!folder || !folder.id) {
                setBreadcrumbs([]);
                return;
            }

            const crumbs = [];
            let current = folder;
            let depth = 0;
            const maxDepth = 10;

            while (current && depth < maxDepth) {
                crumbs.unshift(current);

                if (current.parent_folder_id) {
                    try {
                        const parentResponse = await fileApiRequest(`/folders/${current.parent_folder_id}`);
                        current = parentResponse;
                    } catch (parentErr) {
                        current = null;
                    }
                } else {
                    current = null;
                }
                depth++;
            }

            setBreadcrumbs(crumbs);

        } catch (err) {
            console.error('Failed to load breadcrumbs:', err);
            error('Failed to load folder path');
            setBreadcrumbs([]);
        }
    };

    useEffect(() => {
        loadUploadConfig();
        loadSharedStats();
        loadTrashStats();
    }, []);

    // Validate folder ID from URL on initial load
    useEffect(() => {
        const validateUrlFolder = async () => {
            const folderIdFromUrl = searchParams.get('folder');
            const viewFromUrl = searchParams.get('view');

            // Check for invalid view param
            if (viewFromUrl && !validViews.includes(viewFromUrl)) {
                console.warn('Invalid view param, redirecting to home');
                setSearchParams({}, { replace: true });
                setActiveView('home');
                setCurrentFolder(null);
                return;
            }

            // Validate folder ID if present
            if (folderIdFromUrl && !folderValidated) {
                try {
                    const response = await fileApiRequest(`/folders/${folderIdFromUrl}`);
                    if (response && response.id) {
                        setFolderValidated(true);
                    } else {
                        throw new Error('Folder not found');
                    }
                } catch (err) {
                    console.warn('Invalid folder ID in URL, redirecting to home:', err);
                    error('Folder not found');
                    setSearchParams({}, { replace: true });
                    setCurrentFolder(null);
                    setActiveView('home');
                    setFolderValidated(true);
                }
            }
        };

        validateUrlFolder();
    }, []);

    // Sync state with URL when browser back/forward is used
    useEffect(() => {
        const folderFromUrl = searchParams.get('folder') || null;
        const viewFromUrl = searchParams.get('view') || 'home';

        // Only update if different from current state (to avoid loops)
        if (folderFromUrl !== currentFolder) {
            setCurrentFolder(folderFromUrl);
            setSelectedItems([]);
            setSelectionMode(false);
        }
        if (validViews.includes(viewFromUrl) && viewFromUrl !== activeView) {
            setActiveView(viewFromUrl);
            setSelectedItems([]);
            setSelectionMode(false);
        }
    }, [searchParams]);

    useEffect(() => {
        loadData();
        if (activeView === 'trash') {
            loadTrashStats();
        }
    }, [currentFolder, activeView, filters]);

    useEffect(() => {
        loadBreadcrumbs();
    }, [currentFolder]);


    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if the click is inside the context menu
            const contextMenuElement = document.querySelector('[data-context-menu]');
            if (contextMenuElement && contextMenuElement.contains(event.target)) {
                return; // Don't close if clicking inside context menu
            }

            // Check if the click is inside the filter panel or its trigger button
            const filterPanel = document.querySelector('[data-filter-panel]');
            const filterButton = document.querySelector('[data-filter-button]');

            if (filterPanel && (filterPanel.contains(event.target) || filterButton?.contains(event.target))) {
                return; // Don't close if clicking inside filter panel or its button
            }

            // Close context menu if clicking outside
            if (contextMenu) {
                setContextMenu(null);
            }

            // Close filter panel if clicking outside
            if (showFilterPanel) {
                setShowFilterPanel(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [contextMenu, showFilterPanel]);

    const getFileIcon = (file) => {
        if (file.is_shared_with_me) {
            return <Share2 className="h-8 w-8 text-blue-500" />;
        }

        const extension = file.original_name?.split('.').pop()?.toLowerCase();

        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
            return <FileText className="h-8 w-8 text-green-500" />;
        } else if (['pdf'].includes(extension)) {
            return <FileText className="h-8 w-8 text-red-500" />;
        } else if (['doc', 'docx'].includes(extension)) {
            return <FileText className="h-8 w-8 text-blue-500" />;
        } else if (['xls', 'xlsx'].includes(extension)) {
            return <FileText className="h-8 w-8 text-green-500" />;
        } else {
            return <FileText className="h-8 w-8 text-gray-500" />;
        }
    };

    const validateFile = (file) => {
        if (file.size > uploadConfig.max_file_size) {
            return {
                valid: false,
                error: `File too large (max ${uploadConfig.max_file_size_display || '100MB'})`
            };
        }

        if (!file.name.includes('.')) {
            return { valid: false, error: 'File must have an extension' };
        }

        const extension = file.name.split('.').pop().toLowerCase();
        const allowedExtensions = uploadConfig.allowed_extensions || [];

        if (allowedExtensions.length > 0 && !allowedExtensions.includes(extension)) {
            return { valid: false, error: `File type .${extension} is not allowed` };
        }

        return { valid: true };
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleFileUpload = async (event) => {
        const uploadedFiles = Array.from(event.target.files);
        if (uploadedFiles.length === 0) return;

        const errors = [];
        const validFiles = [];

        uploadedFiles.forEach(file => {
            const validation = validateFile(file);
            if (!validation.valid) {
                errors.push(`${file.name}: ${validation.error}`);
            } else {
                validFiles.push(file);
            }
        });

        if (errors.length > 0) {
            error(`Validation errors:\n${errors.join('\n')}`);
        }

        if (validFiles.length === 0) {
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        setIsUploading(true);
        setUploadProgress({
            show: true,
            overall: 0,
            current: 0,
            total: validFiles.length,
            currentFile: '',
            status: 'uploading',
            message: `Preparing to upload ${validFiles.length} file(s)...`,
            files: validFiles.map(f => ({ name: f.name, status: 'pending', size: f.size }))
        });

        try {
            const results = [];

            for (let i = 0; i < validFiles.length; i++) {
                const file = validFiles[i];

                setUploadProgress(prev => ({
                    ...prev,
                    current: i + 1,
                    currentFile: file.name,
                    overall: ((i / validFiles.length) * 100),
                    message: `Uploading ${file.name} (${i + 1}/${validFiles.length})...`,
                    files: prev.files.map((f, idx) =>
                        idx === i ? { ...f, status: 'uploading' } : f
                    )
                }));

                try {
                    const formData = new FormData();
                    formData.append('file', file);
                    if (currentFolder) formData.append('folder_id', currentFolder);

                    const response = await fetch(`${FILE_APP_URL}/files`, {
                        method: 'POST',
                        credentials: 'include',
                        headers: {
                            'X-CSRF-TOKEN': getCookie('csrf_access_token')
                        },
                        body: formData
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.error || 'Upload failed');
                    }

                    const result = await response.json();
                    results.push({ file: file.name, success: true, result });

                    setUploadProgress(prev => ({
                        ...prev,
                        files: prev.files.map((f, idx) =>
                            idx === i ? { ...f, status: 'success' } : f
                        )
                    }));

                } catch (fileError) {
                    results.push({ file: file.name, success: false, error: fileError.message });

                    setUploadProgress(prev => ({
                        ...prev,
                        files: prev.files.map((f, idx) =>
                            idx === i ? { ...f, status: 'error', error: fileError.message } : f
                        )
                    }));
                }
            }

            const successful = results.filter(r => r.success).length;
            const failed = results.filter(r => !r.success).length;

            setUploadProgress(prev => ({
                ...prev,
                overall: 100,
                status: failed === 0 ? 'success' : (successful === 0 ? 'error' : 'warning'),
                message: failed === 0
                    ? `All ${successful} file(s) uploaded successfully!`
                    : successful === 0
                        ? 'All uploads failed'
                        : `${successful} file(s) uploaded, ${failed} failed`,
                currentFile: ''
            }));

            if (failed === 0) {
                setTimeout(() => {
                    setUploadProgress(prev => ({ ...prev, show: false }));
                }, 3000);
            }

            if (successful > 0) {
                loadData();
            }

            if (failed > 0 && successful > 0) {
                const failedFiles = results.filter(r => !r.success).map(r => r.file).join(', ');
                error(`Failed uploads: ${failedFiles}`);
            }

        } catch (err) {
            setUploadProgress(prev => ({
                ...prev,
                status: 'error',
                message: `Upload error: ${err.message}`
            }));
            error(`Upload error: ${err.message}`);
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const closeProgress = () => {
        setUploadProgress(prev => ({ ...prev, show: false }));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);

        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length > 0) {
            const event = { target: { files: droppedFiles } };
            handleFileUpload(event);
        }
    };

    const createFolder = async (name, description = '') => {
        try {
            await fileApiRequest('/folders', {
                method: 'POST',
                headers: {
                    'content-type': 'application/json',
                    'X-CSRF-TOKEN': getCookie('csrf_access_token')
                },
                body: JSON.stringify({
                    name,
                    description,
                    parent_folder_id: currentFolder
                })
            });

            success('Folder created successfully!');
            loadData();
            setShowCreateFolder(false);

        } catch (err) {
            error(`Failed to create folder: ${err.message}`);
        }
    };

    const toggleStar = async (item, isFolder = false) => {
        try {
            const endpoint = isFolder ? `/folders/${item.id}/star` : `/files/${item.id}/star`;
            await fileApiRequest(endpoint, {
                method: 'POST',
                headers: {
                    'X-CSRF-TOKEN': getCookie('csrf_access_token')
                }
            });

            success(`${isFolder ? 'Folder' : 'File'} ${item.is_starred ? 'unstarred' : 'starred'}!`);
            loadData();

        } catch (err) {
            error(`Failed to update star status: ${err.message}`);
        }
    };

    const deleteItem = async (item, isFolder = false) => {
        choice(`Are you sure you want to delete ${item.name}?`, async () => {
            try {
                const endpoint = isFolder ? `/folders/${item.id}` : `/files/${item.id}`;
                await fileApiRequest(endpoint, {
                    method: 'DELETE',
                    headers: {
                        'X-CSRF-TOKEN': getCookie('csrf_access_token')
                    }
                });

                success(`${isFolder ? 'Folder' : 'File'} deleted successfully!`);
                loadData();
                loadTrashStats();

            } catch (err) {
                error(`Failed to delete: ${err.message}`);
            }
        });
    };

    const restoreItem = async (item, isFolder = false) => {
        choice(`Are you sure you want to restore ${item.name}?`, async () => {
            try {
                const endpoint = isFolder ? `/folders/${item.id}` : `/files/${item.id}`;
                await fileApiRequest(endpoint, {
                    method: 'PATCH',
                    headers: {
                        'content-type': 'application/json',
                        'X-CSRF-TOKEN': getCookie('csrf_access_token')
                    },
                    body: JSON.stringify({
                        'action': 'restore'
                    })
                });

                success(`${isFolder ? 'Folder' : 'File'} restored successfully!`);
                loadData();
                loadTrashStats();

            } catch (err) {
                error(`Failed to restore: ${err.message}`);
            }
        });
    };

    const loadTrashStats = async () => {
        try {
            const stats = await fileApiRequest('/trash');
            setTrashStats(stats);
        } catch (err) {
            console.error('Failed to load trash stats:', err);
        }
    };

    const emptyTrash = async () => {
        if (trashStats.deleted_files_count === 0 && trashStats.deleted_folders_count === 0) {
            error('Trash is already empty');
            return;
        }

        choice(
            `Are you sure you want to permanently delete all ${trashStats.deleted_files_count} files and ${trashStats.deleted_folders_count} folders?\n\nThis will free ${trashStats.total_size_formatted} of storage.\n\nThis action cannot be undone!`,
            async () => {
                setLoading(true);
                try {
                    const result = await fileApiRequest('/trash', {
                        method: 'DELETE',
                        headers: {
                            'X-CSRF-TOKEN': getCookie('csrf_access_token')
                        }
                    });

                    success(`Trash emptied! ${result.files_deleted} files and ${result.folders_deleted} folders permanently deleted. Freed ${result.storage_freed_formatted}.`);
                    loadData();
                    loadTrashStats();

                } catch (err) {
                    error(`Failed to empty trash: ${err.message}`);
                } finally {
                    setLoading(false);
                }
            }
        );
    };

    const restoreAllTrash = async () => {
        if (trashStats.deleted_files_count === 0 && trashStats.deleted_folders_count === 0) {
            error('Trash is already empty');
            return;
        }

        choice(
            `Are you sure you want to restore all ${trashStats.deleted_files_count} files and ${trashStats.deleted_folders_count} folders?`,
            async () => {
                setLoading(true);
                try {
                    const result = await fileApiRequest('/trash', {
                        method: 'PATCH',
                        headers: {
                            'X-CSRF-TOKEN': getCookie('csrf_access_token')
                        }
                    });

                    success(`All items restored! ${result.files_restored} files and ${result.folders_restored} folders recovered.`);
                    loadData();
                    loadTrashStats();

                } catch (err) {
                    error(`Failed to restore trash: ${err.message}`);
                } finally {
                    setLoading(false);
                }
            }
        );
    };

    const copyItem = async (item, isFolder = false) => {
        choice(`Create a copy of "${item.name}"?`, async () => {
            setLoading(true);
            try {
                const endpoint = isFolder ? `/folders/${item.id}` : `/files/${item.id}`;
                const result = await fileApiRequest(endpoint, {
                    method: 'PATCH',
                    headers: {
                        'content-type': 'application/json',
                        'X-CSRF-TOKEN': getCookie('csrf_access_token')
                    },
                    body: JSON.stringify({
                        'action': 'copy',
                        'target_folder_id': currentFolder
                    })
                });

                success(`${isFolder ? 'Folder' : 'File'} copied successfully!`);
                loadData();

            } catch (err) {
                error(`Failed to copy: ${err.message}`);
            } finally {
                setLoading(false);
            }
        });
    };

    const downloadFile = async (file) => {
        try {
            const response = await fileApiRequest(`/files/${file.id}/download`);

            if (response.download_url) {
                const link = document.createElement('a');
                link.href = response.download_url;
                link.download = response.filename || file.original_name || file.name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                success('Download started!');
            } else {
                throw new Error('No download URL received');
            }

        } catch (err) {
            error(`Download failed: ${err.message}`);
        }
    };

    const shareItem = async (item, isFolder = false) => {
        setSelectedItem({ ...item, isFolder });
        setExistingShare(null); // Reset first

        // Try to fetch existing public share
        try {
            const endpoint = isFolder ?
                `/folders/${item.id}/share` :
                `/files/${item.id}/share`;

            const shares = await fileApiRequest(endpoint, {
                method: 'GET'
            });

            // Find existing public share
            if (shares && Array.isArray(shares)) {
                const publicShare = shares.find(s => s.is_public);
                if (publicShare) {
                    setExistingShare(publicShare);
                }
            }
        } catch (err) {
            // If endpoint doesn't exist or fails, just continue without existing share
            console.log('Could not fetch existing shares:', err);
        }

        setShowShareModal(true);
    };

    const renameItem = async (item, isFolder = false) => {
        setSelectedItem({ ...item, isFolder });
        setShowRenameModal(true);
    };

    const moveItem = async (item, isFolder = false) => {
        setSelectedItem({ ...item, isFolder });
        setShowMoveModal(true);
    };

    const navigateToFolder = (folderId) => {
        setCurrentFolder(folderId);
        setSelectedItems([]);
        setSelectionMode(false);
        setFolderValidated(true); // Mark as validated since user navigated manually

        // Update URL
        const newParams = new URLSearchParams(searchParams);
        if (folderId) {
            newParams.set('folder', folderId);
        } else {
            newParams.delete('folder');
        }
        // Keep view param if not home
        if (activeView === 'home') {
            newParams.delete('view');
        }
        setSearchParams(newParams, { replace: true });
    };

    const navigateToView = (view) => {
        setActiveView(view);
        setCurrentFolder(null);
        setSelectedItems([]);
        setSelectionMode(false);
        setFolderValidated(true);

        // Update URL
        const newParams = new URLSearchParams();
        if (view && view !== 'home') {
            newParams.set('view', view);
        }
        setSearchParams(newParams, { replace: true });
    };

    const handleContextMenu = (e, item) => {
        e.preventDefault();
        e.stopPropagation();

        if (!item) {
            console.warn('No item provided to context menu');
            return;
        }

        const isFolder = !item.file_size && item.created_by;

        let actions = [
            {
                label: 'View',
                action: () => !isFolder ? viewFile(item) : navigateToFolder(item.id),
                icon: Eye
            },
        ];

        if (!isFolder) {
            actions.push({
                label: 'Download',
                action: () => downloadFile(item),
                icon: Download
            });
        }

        actions.push({
            label: item.is_starred ? 'Unstar' : 'Star',
            action: () => toggleStar(item, isFolder),
            icon: Star
        });

        if (item.is_deleted) {
            actions.push(
                { label: 'Restore', action: () => restoreItem(item, isFolder), icon: FiUpload },
            );
        }

        if (!item.is_shared_with_me) {
            actions.push(
                { label: 'Share', action: () => shareItem(item, isFolder), icon: Share2 },
                { label: 'Rename', action: () => renameItem(item, isFolder), icon: Edit },
                { label: 'Move', action: () => moveItem(item, isFolder), icon: Move },
                { label: 'Copy', action: () => copyItem(item, isFolder), icon: Copy },
                { label: 'Delete', action: () => deleteItem(item, isFolder), icon: Trash2, dangerous: true }
            );
        } else {
            if (item.share_permission === 'edit') {
                actions.push(
                    { label: 'Rename', action: () => renameItem(item, isFolder), icon: Edit }
                );
            }

            if (item.shared_by_username) {
                actions.push({
                    label: `Shared by ${item.shared_by_username}`,
                    action: () => { },
                    icon: Share2,
                    disabled: true
                });
            }
        }

        setContextMenu({
            x: e.clientX,
            y: e.clientY,
            actions,
            item
        });
        setSelectedItem(item);
    };

    const renderEmptyState = () => {
        const emptyMessages = {
            home: {
                title: "No files yet",
                description: "Upload your first file to get started",
                action: "Upload Files"
            },
            shared: {
                title: "No shared files",
                description: "Files shared with you will appear here",
                action: null
            },
            starred: {
                title: "No starred items",
                description: "Star files and folders to find them here",
                action: null
            },
            trash: {
                title: "Trash is empty",
                description: "Deleted files will appear here",
                action: null
            }
        };

        const message = emptyMessages[activeView] || emptyMessages.home;

        return (
            <div className="text-center py-12">
                <FolderOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{message.title}</h3>
                <p className="text-gray-500 mb-6">{message.description}</p>
                {message.action && (
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        <Upload className="h-4 w-4 mr-2" />
                        {message.action}
                    </button>
                )}
            </div>
        );
    };

    const allItems = useMemo(() => {
        const safeFiles = (files || []).filter(file => file && file.id);
        const safeFolders = (folders || []).filter(folder => folder && folder.id);

        let items = [
            ...safeFolders.map(folder => ({ ...folder, type: 'folder' })),
            ...safeFiles.map(file => ({ ...file, type: 'file' }))
        ];

        if (filters.search || searchTerm) {
            const searchKey = filters.search || searchTerm;
            const searchLower = searchKey.toLowerCase();
            items = items.filter(item => {
                const name = item.name || '';
                const description = item.description || '';
                const tags = item.tags || '';

                return name.toLowerCase().includes(searchLower) ||
                    description.toLowerCase().includes(searchLower) ||
                    tags.toLowerCase().includes(searchLower);
            });
        }

        if (filters.fileType && filters.fileType !== 'all') {

            items = items.filter(item => {
                return item.type === 'folder' || item.mime_type.includes(filters.fileType)
            }
            );
        }

        if (filters.starred) {
            items = items.filter(item => item.is_starred);
        }

        if (filters.dateRange) {
            const now = new Date();
            let cutoffDate;

            switch (filters.dateRange) {
                case 'today':
                    cutoffDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    break;
                case 'week':
                    cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                    break;
                case 'month':
                    cutoffDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
                    break;
                default:
                    cutoffDate = null;
            }

            if (cutoffDate) {
                items = items.filter(item => {
                    const itemDate = new Date(item.updated_at || item.created_at);
                    return itemDate >= cutoffDate;
                });
            }
        }

        return items.filter(item => item && item.id);
    }, [files, folders, filters, searchTerm]);

    return (
        <div className="min-h-screen bg-gray-50 flex">
            {showFileViewer && currentViewerFile && (
                <FileViewer
                    file={currentViewerFile}
                    isOpen={showFileViewer}
                    onClose={() => {
                        setShowFileViewer(false);
                        setCurrentViewerFile(null);
                    }}
                    onDownload={handleViewerDownload}
                    onShare={handleViewerShare}
                    onStar={handleViewerStar}
                    onNext={() => navigateViewerFile('next')}
                    onPrevious={() => navigateViewerFile('previous')}
                    hasNext={viewerFileIndex < files.length - 1}
                    hasPrevious={viewerFileIndex > 0}
                />
            )}
            {uploadProgress.show && (
                <UploadProgressBar
                    progress={uploadProgress}
                    onClose={closeProgress}
                    sidebarOpen={sidebarOpen}
                    position="top"
                />
            )}

            {/* Batch Actions Bar */}
            {showBatchActions && (
                <div
                    className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg"
                    style={{ zIndex: 99999 }}
                >
                    <div className="px-6 py-4">
                        <div className="flex items-center justify-between max-w-7xl mx-auto">
                            <div className="flex items-center space-x-4">
                                <span className="text-sm font-medium text-gray-900">
                                    {selectedItems.length} item(s) selected
                                </span>
                                <button
                                    onClick={() => {
                                        setSelectedItems([]);
                                        setSelectionMode(false);
                                    }}
                                    className="text-sm text-gray-500 hover:text-gray-700"
                                >
                                    Clear selection
                                </button>
                            </div>
                            <div className="flex items-center space-x-3">
                                {activeView === 'trash' && selectedItems.some(item => item.is_deleted) && (
                                    <button
                                        onClick={batchRestore}
                                        className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                                    >
                                        <RotateCcw className="h-4 w-4 mr-2" />
                                        Restore Selected
                                    </button>
                                )}
                                <button
                                    onClick={batchDelete}
                                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete Selected
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Sidebar */}
            <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform 
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
                transition-transform duration-300 ease-in-out 
                lg:translate-x-0 lg:static lg:inset-0
                ${showFileViewer ? 'lg:-translate-x-full lg:w-0 lg:opacity-0' : ''}`}>
                <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                        <div className="h-8 w-8 bg-white rounded-lg flex items-center justify-center">
                            <img src={logo} alt="TimeFiles Logo" />
                        </div>
                        <span className="font-bold text-xl text-gray-900">TimeFiles</span>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden p-1 rounded-md hover:bg-gray-100"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <nav className="mt-6 px-4">
                    <div className="space-y-2">
                        <button
                            onClick={() => navigateToView('home')}
                            className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg ${activeView === 'home' ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:bg-gray-100'
                                }`}
                        >
                            <Home className="h-5 w-5 mr-3" />
                            Home
                        </button>
                        <button
                            onClick={() => navigateToView('starred')}
                            className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg ${activeView === 'starred' ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:bg-gray-100'
                                }`}
                        >
                            <Star className="h-5 w-5 mr-3" />
                            Starred
                        </button>
                        <button
                            onClick={() => navigateToView('shared')}
                            className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg ${activeView === 'shared' ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:bg-gray-100'
                                }`}
                        >
                            <div className="flex items-center">
                                <Share2 className="h-5 w-5 mr-3" />
                                Shared
                            </div>
                        </button>
                        <button
                            onClick={() => navigateToView('trash')}
                            className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg ${activeView === 'trash' ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:bg-gray-100'
                                }`}
                        >
                            <div className="flex items-center">
                                <Trash2 className="h-5 w-5 mr-3" />
                                Trash
                            </div>
                            {(trashStats.deleted_files_count > 0 || trashStats.deleted_folders_count > 0) && (
                                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                                    {trashStats.deleted_files_count + trashStats.deleted_folders_count}
                                </span>
                            )}
                        </button>
                    </div>

                    <div className="mt-8">
                        <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Quick Stats</h3>
                        <div className="mt-3 space-y-3">
                            <div className="flex items-center px-3 py-2 text-sm text-gray-600">
                                <Cloud className="h-4 w-4 mr-3 text-blue-500" />
                                <span>{stats.fileCount} Files</span>
                            </div>
                            <div className="flex items-center px-3 py-2 text-sm text-gray-600">
                                <FolderOpen className="h-4 w-4 mr-3 text-yellow-500" />
                                <span>{stats.folderCount} Folders</span>
                            </div>
                            <div className="flex items-center px-3 py-2 text-sm text-gray-600">
                                <Shield className="h-4 w-4 mr-3 text-green-500" />
                                <span>All Secure</span>
                            </div>
                        </div>
                    </div>
                </nav>

                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 bg-gradient-to-br from-gray-600 to-gray-700 rounded-full flex items-center justify-center">
                            <User className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{user?.username || 'User'}</p>
                            <p className="text-xs text-gray-500 truncate">{user?.role || 'Member'}</p>
                        </div>
                        <button
                            onClick={() => {
                                logout().then(
                                    navigate('/login')
                                )
                            }}
                            className="p-1 rounded-md hover:bg-gray-100"
                            title="Logout"
                        >
                            <LogOut className="h-4 w-4 text-gray-400" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col lg:ml-0">
                {/* Header */}
                <header className="bg-white shadow-sm border-b border-gray-200">
                    <div className="flex items-center justify-between h-16 px-6">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => setSidebarOpen(true)}
                                className="lg:hidden p-2 rounded-md hover:bg-gray-100"
                            >
                                <Menu className="h-5 w-5" />
                            </button>

                            <nav className="flex items-center space-x-2 text-sm overflow-hidden">
                                <button
                                    onClick={() => navigateToFolder(null)}
                                    className="flex items-center px-2 py-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                >
                                    <Home className="h-4 w-4 mr-1" />
                                    <span className="hidden sm:inline">Home</span>
                                </button>

                                {breadcrumbs.map((crumb, index) => (
                                    <React.Fragment key={crumb.id}>
                                        <ChevronRight className="h-4 w-4 text-gray-400" />
                                        <button
                                            onClick={() => navigateToFolder(crumb.id)}
                                            className="flex items-center px-2 py-1 font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors max-w-xs truncate"
                                            title={crumb.name}
                                        >
                                            {crumb.color && (
                                                <div
                                                    className="w-3 h-3 rounded-sm mr-2 flex-shrink-0"
                                                    style={{ backgroundColor: crumb.color }}
                                                />
                                            )}
                                            <Folder className="h-4 w-4 mr-1 flex-shrink-0" />
                                            <span className="truncate">{crumb.name}</span>
                                        </button>
                                    </React.Fragment>
                                ))}
                            </nav>
                        </div>

                        <div className="flex items-center space-x-4">
                            <button
                                onClick={loadData}
                                className="p-2 rounded-md hover:bg-gray-100"
                                disabled={loading}
                            >
                                <RefreshCw className={`h-5 w-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                            <NotificationInbox
                                fileApiRequest={fileApiRequest}
                                onNavigate={(url) => {
                                    // Handle navigation from notification action
                                    if (url.includes('view=shared')) {
                                        navigateToView('shared');
                                    } else if (url.includes('folder=')) {
                                        const folderId = new URLSearchParams(url.split('?')[1]).get('folder');
                                        if (folderId) {
                                            navigateToFolder(folderId);
                                        }
                                    }
                                }}
                                onViewAll={() => setShowFullInbox(true)}
                            />
                            <button className="p-2 rounded-md hover:bg-gray-100">
                                <Settings className="h-5 w-5 text-gray-600" />
                            </button>
                        </div>
                    </div>
                </header>

                {/* Toolbar */}
                <div className="bg-white border-b border-gray-200 px-6 py-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                        <div className="flex items-center flex-wrap gap-2">
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="inline-flex items-center whitespace-nowrap px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                                disabled={isUploading}
                            >
                                <Upload className="h-4 w-4 mr-1.5" />
                                {isUploading ? 'Uploading...' : 'Upload'}
                            </button>
                            <button
                                onClick={() => setShowCreateFolder(true)}
                                className="inline-flex items-center whitespace-nowrap px-3 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
                            >
                                <FolderPlus className="h-4 w-4 mr-1.5" />
                                New Folder
                            </button>
                            <button
                                onClick={toggleSelectionMode}
                                className={`inline-flex items-center whitespace-nowrap px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${selectionMode
                                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                <CheckSquare className="h-4 w-4 mr-1.5" />
                                {selectionMode ? 'Exit' : 'Select'}
                            </button>
                            {activeView === 'trash' && (trashStats.deleted_files_count > 0 || trashStats.deleted_folders_count > 0) && (
                                <>
                                    <button
                                        onClick={restoreAllTrash}
                                        className="inline-flex items-center whitespace-nowrap px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
                                        disabled={loading}
                                    >
                                        <RotateCcw className="h-4 w-4 mr-1.5" />
                                        Restore All
                                    </button>
                                    <button
                                        onClick={emptyTrash}
                                        className="inline-flex items-center whitespace-nowrap px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
                                        disabled={loading}
                                    >
                                        <Trash2 className="h-4 w-4 mr-1.5" />
                                        Empty ({trashStats.total_size_formatted})
                                    </button>
                                </>
                            )}
                        </div>

                        <div className="flex items-center space-x-4">
                            {selectionMode && (
                                <button
                                    onClick={selectAll}
                                    className="inline-flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                                >
                                    {getSelectionStatus() === 'all' ? (
                                        <>
                                            <Minus className="h-4 w-4 mr-1" />
                                            Deselect All
                                        </>
                                    ) : (
                                        <>
                                            <CheckSquare className="h-4 w-4 mr-1" />
                                            Select All
                                        </>
                                    )}
                                </button>
                            )}

                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search files..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                                />
                            </div>

                            <div className="flex items-center space-x-2">
                                <div className="relative">
                                    <button
                                        data-filter-button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowFilterPanel(!showFilterPanel);
                                        }}
                                        className="p-2 rounded-md hover:bg-gray-100"
                                    >
                                        <Filter className="h-4 w-4 text-gray-600" />
                                    </button>
                                    {showFilterPanel && (
                                        <div data-filter-panel onClick={(e) => e.stopPropagation()}>
                                            <FilterPanel
                                                filters={filters}
                                                setFilters={setFilters}
                                                onClose={() => setShowFilterPanel(false)}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="border-l border-gray-300 h-6"></div>
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100 text-gray-600'}`}
                                >
                                    <Grid className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100 text-gray-600'}`}
                                >
                                    <List className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* File Grid/List */}
                <main
                    className={`flex-1 p-6 ${showBatchActions ? 'pb-24' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {/* Drag overlay */}
                    {dragOver && (
                        <div className="fixed inset-0 bg-blue-600 bg-opacity-20 flex items-center justify-center z-40">
                            <div className="bg-white rounded-lg p-8 shadow-xl border-2 border-dashed border-blue-400">
                                <Upload className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                                <p className="text-xl font-semibold text-gray-800 text-center">Drop files here to upload</p>
                                <p className="text-sm text-gray-500 text-center mt-2">
                                    Maximum file size: {uploadConfig.max_file_size_display || formatFileSize(uploadConfig.max_file_size) || '100MB'}
                                </p>
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                            <span className="ml-2 text-gray-600">Loading...</span>
                        </div>
                    ) : viewMode === 'grid' ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {allItems.map((item) => {
                                const isSelected = selectedItems.some(selected => selected.id === item.id && selected.type === item.type);

                                return (
                                    <div
                                        key={item.id}
                                        className={`group relative bg-white border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-blue-200 ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                                            }`}
                                        onClick={(e) => {
                                            if (selectionMode) {
                                                e.preventDefault();
                                                toggleItemSelection(item);
                                                return;
                                            }

                                            if (e.button === 2) return;

                                            const isFolder = !item.file_size && item.created_by;
                                            if (isFolder) {
                                                navigateToFolder(item.id);
                                            } else {
                                                const fileIndex = files.findIndex(f => f.id === item.id);
                                                viewFile(item, fileIndex >= 0 ? fileIndex : 0);
                                            }
                                        }}
                                        onContextMenu={(e) => !selectionMode && handleContextMenu(e, item)}
                                    >
                                        {/* Selection checkbox */}
                                        {selectionMode && (
                                            <div className="absolute top-2 left-2 z-10">
                                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected
                                                    ? 'bg-blue-600 border-blue-600'
                                                    : 'bg-white border-gray-300 hover:border-blue-400'
                                                    }`}>
                                                    {isSelected && (
                                                        <CheckSquare className="h-3 w-3 text-white" />
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex flex-col items-center text-center">
                                            <div className="mb-3 relative">
                                                {item.type === 'folder' ?
                                                    <FolderOpen className="h-8 w-8 text-blue-600" /> :
                                                    getFileIcon(item)
                                                }
                                                {item.is_starred && (
                                                    <Star className="absolute -top-1 -right-1 h-4 w-4 text-yellow-500 fill-current" />
                                                )}
                                            </div>

                                            <h3 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2 leading-tight">
                                                {item.name}
                                            </h3>
                                            <p className="text-xs text-gray-500">
                                                {item.type === 'folder' ? `${item.file_count + item.subfolder_count || 0} items` : item.formatted_size || formatFileSize(item.file_size || 0)}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                {new Date(item.created_at).toLocaleDateString()}
                                            </p>
                                        </div>

                                        {!selectionMode && (
                                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <div className="flex items-center space-x-1">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleStar(item, item.type === 'folder');
                                                        }}
                                                        className="p-1 rounded-md hover:bg-gray-100"
                                                    >
                                                        <Star className={`h-3 w-3 ${item.is_starred ? 'text-yellow-500 fill-current' : 'text-gray-400'}`} />
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleContextMenu(e, item);
                                                        }}
                                                        className="p-1 rounded-md hover:bg-gray-100"
                                                    >
                                                        <MoreVertical className="h-3 w-3 text-gray-400" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {selectionMode && (
                                            <th className="px-6 py-3 text-left">
                                                <button
                                                    onClick={selectAll}
                                                    className="flex items-center justify-center w-5 h-5"
                                                >
                                                    {getSelectionStatus() === 'all' ? (
                                                        <CheckSquare className="h-4 w-4 text-blue-600" />
                                                    ) : getSelectionStatus() === 'partial' ? (
                                                        <Minus className="h-4 w-4 text-blue-600" />
                                                    ) : (
                                                        <Square className="h-4 w-4 text-gray-400" />
                                                    )}
                                                </button>
                                            </th>
                                        )}
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modified</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {allItems.map((item) => {
                                        const isSelected = selectedItems.some(selected => selected.id === item.id && selected.type === item.type);

                                        return (
                                            <tr
                                                key={item.id}
                                                className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                                                onClick={(e) => {
                                                    if (selectionMode) {
                                                        e.preventDefault();
                                                        toggleItemSelection(item);
                                                        return;
                                                    }

                                                    if (e.button === 2) return;

                                                    const isFolder = !item.file_size && item.created_by;
                                                    if (isFolder) {
                                                        navigateToFolder(item.id);
                                                    } else {
                                                        const fileIndex = files.findIndex(f => f.id === item.id);
                                                        viewFile(item, fileIndex >= 0 ? fileIndex : 0);
                                                    }
                                                }}
                                                onContextMenu={(e) => !selectionMode && handleContextMenu(e, item)}
                                            >
                                                {selectionMode && (
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected
                                                            ? 'bg-blue-600 border-blue-600'
                                                            : 'bg-white border-gray-300 hover:border-blue-400'
                                                            }`}>
                                                            {isSelected && (
                                                                <CheckSquare className="h-3 w-3 text-white" />
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0 mr-3">
                                                            {item.type === 'folder' ?
                                                                <FolderOpen className="h-8 w-8 text-blue-600" /> :
                                                                getFileIcon(item)
                                                            }
                                                        </div>
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900 flex items-center">
                                                                {item.name}
                                                                {item.is_starred && (
                                                                    <Star className="ml-2 h-4 w-4 text-yellow-500 fill-current" />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {item.type === 'folder' ? `${item.file_count + item.subfolder_count || 0} items` : item.formatted_size || formatFileSize(item.file_size || 0)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {new Date(item.created_at).toLocaleDateString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    {!selectionMode && (
                                                        <div className="flex items-center justify-end space-x-2">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleStar(item, item.type === 'folder');
                                                                }}
                                                                className="text-gray-400 hover:text-yellow-500"
                                                            >
                                                                <Star className={`h-4 w-4 ${item.is_starred ? 'text-yellow-500 fill-current' : ''}`} />
                                                            </button>
                                                            {item.type === 'file' && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        downloadFile(item);
                                                                    }}
                                                                    className="text-gray-400 hover:text-gray-600"
                                                                >
                                                                    <Download className="h-4 w-4" />
                                                                </button>
                                                            )}

                                                            {(!item.is_shared || (item.is_shared && item.uploaded_by == user?.id)) &&
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        shareItem(item, item.type === 'folder');
                                                                    }}
                                                                    className="text-gray-400 hover:text-gray-600"
                                                                >
                                                                    <Share2 className="h-4 w-4" />
                                                                </button>
                                                            }
                                                            {item.is_deleted && <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    restoreItem(item, item.type === 'folder');
                                                                }}
                                                                className="text-gray-400 hover:text-green-500"
                                                            >
                                                                <FiUpload className="h-4 w-4" />
                                                            </button>}

                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    deleteItem(item, item.type === 'folder');
                                                                }}
                                                                className="text-gray-400 hover:text-red-500"
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {allItems.length === 0 && !loading && (
                        renderEmptyState()
                    )}
                </main>
            </div>

            {/* Hidden file input for uploads */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                accept="*/*"
            />

            {/* Context Menu */}
            {/* Context Menu */}
            {contextMenu && (
                <div
                    data-context-menu
                    className="fixed bg-white shadow-lg rounded-lg border border-gray-200 py-2 z-50 min-w-48"
                    style={{
                        top: contextMenu.y,
                        left: contextMenu.x,
                        transform: 'translate(-25%, 0)'
                    }}
                // Remove this problematic onClick handler:
                // onClick={(e) => {
                //     e.stopPropagation();
                //     e.preventDefault();
                // }}
                >
                    {contextMenu.actions && contextMenu.actions.map((action, index) => (
                        <button
                            key={`${action.label}-${index}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();

                                // Close the context menu first
                                setContextMenu(null);

                                // Then execute the action
                                if (!action.disabled && action.action) {
                                    action.action();
                                }
                            }}
                            disabled={action.disabled}
                            className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-3 transition-colors ${action.disabled
                                ? 'text-gray-400 cursor-not-allowed'
                                : action.dangerous
                                    ? 'text-red-600 hover:bg-red-50 cursor-pointer'
                                    : 'text-gray-700 hover:bg-gray-100 cursor-pointer'
                                }`}
                        >
                            {action.icon && <action.icon className="h-4 w-4" />}
                            <span>{action.label}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* Create Folder Modal */}
            {showCreateFolder && (
                <CreateFolderModal
                    onClose={() => setShowCreateFolder(false)}
                    onSubmit={createFolder}
                />
            )}

            {/* Share Modal */}
            {showShareModal && selectedItem && (
                <ShareModal
                    item={selectedItem}
                    existingShare={existingShare}
                    onClose={() => {
                        setShowShareModal(false);
                        setSelectedItem(null);
                        setExistingShare(null);
                    }}
                    onShare={async (shareData) => {
                        try {
                            const endpoint = selectedItem.isFolder ?
                                `/folders/${selectedItem.id}/share` :
                                `/files/${selectedItem.id}/share`;

                            // fileApiRequest already parses JSON and returns the result directly
                            const result = await fileApiRequest(endpoint, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'X-CSRF-TOKEN': getCookie('csrf_access_token')
                                },
                                body: JSON.stringify(shareData)
                            });

                            if (result) {
                                success('Item shared successfully!');

                                // If public link, keep modal open to show the link
                                if (shareData.is_public) {
                                    return result;
                                }

                                setShowShareModal(false);
                                setSelectedItem(null);
                                return result;
                            }
                        } catch (err) {
                            error(err);
                        }
                    }}
                    onRevoke={async (shareId) => {
                        try {
                            const endpoint = selectedItem.isFolder ?
                                `/folders/${selectedItem.id}/share/${shareId}` :
                                `/files/${selectedItem.id}/share/${shareId}`;

                            await fileApiRequest(endpoint, {
                                method: 'DELETE',
                                headers: {
                                    'X-CSRF-TOKEN': getCookie('csrf_access_token')
                                }
                            });

                            success('Share link revoked successfully!');
                        } catch (err) {
                            error('Failed to revoke share');
                            throw err;
                        }
                    }}
                    onUpdatePermission={async (shareId, permission) => {
                        try {
                            const endpoint = selectedItem.isFolder ?
                                `/folders/${selectedItem.id}/share/${shareId}` :
                                `/files/${selectedItem.id}/share/${shareId}`;

                            await fileApiRequest(endpoint, {
                                method: 'PATCH',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'X-CSRF-TOKEN': getCookie('csrf_access_token')
                                },
                                body: JSON.stringify({ permission })
                            });

                            success('Permission updated!');
                        } catch (err) {
                            error('Failed to update permission');
                            throw err;
                        }
                    }}
                />
            )}

            {/* Rename Modal */}
            {showRenameModal && selectedItem && (
                <RenameModal
                    item={selectedItem}
                    onClose={() => {
                        setShowRenameModal(false);
                        setSelectedItem(null);
                    }}
                    onRename={async (newName) => {
                        try {
                            const endpoint = selectedItem.isFolder ?
                                `/folders/${selectedItem.id}` :
                                `/files/${selectedItem.id}`;

                            await fileApiRequest(endpoint, {
                                method: 'PUT',
                                headers: {
                                    'content-type': 'application/json',
                                    'X-CSRF-TOKEN': getCookie('csrf_access_token')
                                },
                                body: JSON.stringify({ name: newName })
                            });

                            success(`${selectedItem.isFolder ? 'Folder' : 'File'} renamed successfully!`);
                            setShowRenameModal(false);
                            setSelectedItem(null);
                            loadData();

                        } catch (err) {
                            error(`Failed to rename: ${err.message}`);
                        }
                    }}
                />
            )}

            {/* Move Modal */}
            {showMoveModal && selectedItem && (
                <MoveModal
                    item={selectedItem}
                    currentFolderId={currentFolder}
                    onClose={() => {
                        setShowMoveModal(false);
                        setSelectedItem(null);
                    }}
                    onMove={async (destinationFolderId) => {
                        try {
                            const endpoint = selectedItem.isFolder
                                ? `/folders/${selectedItem.id}`
                                : `/files/${selectedItem.id}`;

                            const body = selectedItem.isFolder
                                ? { parent_folder_id: destinationFolderId }
                                : { folder_id: destinationFolderId };

                            await fileApiRequest(endpoint, {
                                method: 'PUT',
                                headers: {
                                    'content-type': 'application/json',
                                    'X-CSRF-TOKEN': getCookie('csrf_access_token')
                                },
                                body: JSON.stringify(body)
                            });

                            success(`${selectedItem.isFolder ? 'Folder' : 'File'} moved successfully!`);
                            setShowMoveModal(false);
                            setSelectedItem(null);
                            loadData();

                        } catch (err) {
                            error(`Failed to move: ${err.message}`);
                        }
                    }}
                />
            )}

            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                ></div>
            )}

            {/* Full Notification Inbox Modal */}
            <NotificationInboxFull
                isOpen={showFullInbox}
                onClose={() => setShowFullInbox(false)}
                fileApiRequest={fileApiRequest}
                onNavigate={(url) => {
                    // Handle navigation from notification action
                    if (url.includes('view=shared')) {
                        navigateToView('shared');
                    } else if (url.includes('folder=')) {
                        const folderId = new URLSearchParams(url.split('?')[1]).get('folder');
                        if (folderId) {
                            navigateToFolder(folderId);
                        }
                    }
                }}
            />
        </div>
    );
};

// Upload Progress Bar Component
const UploadProgressBar = ({ progress, onClose, position = 'bottom', sidebarOpen = false }) => {
    const getStatusColor = (status) => {
        switch (status) {
            case 'success':
                return 'bg-green-500';
            case 'error':
                return 'bg-red-500';
            case 'warning':
                return 'bg-yellow-500';
            default:
                return 'bg-blue-500';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'success':
                return <CheckCircle className="h-4 w-4 text-green-500" />;
            case 'error':
                return <XCircle className="h-4 w-4 text-red-500" />;
            case 'warning':
                return <AlertCircle className="h-4 w-4 text-yellow-500" />;
            default:
                return <Upload className="h-4 w-4 text-blue-500" />;
        }
    };

    const positionClass = position === 'top' ? 'top-0' : 'bottom-0';

    return (
        <div
            className={`fixed ${positionClass} left-0 right-0 bg-white border-t border-gray-200 shadow-lg backdrop-blur-sm bg-white/95`}
            style={{ zIndex: 9999 }}
        >
            <div className="w-full h-1 bg-gray-200">
                <div
                    className={`h-full transition-all duration-300 ease-out ${getStatusColor(progress.status)}`}
                    style={{ width: `${progress.overall}%` }}
                />
            </div>

            <div className="px-4 py-3">
                <div className="flex items-center justify-between max-w-7xl mx-auto">
                    <div className="flex items-center space-x-3">
                        {getStatusIcon(progress.status)}
                        <div>
                            <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-900">
                                    {progress.status === 'success' ? 'Upload Complete' :
                                        progress.status === 'error' ? 'Upload Failed' :
                                            progress.status === 'warning' ? 'Upload Warning' :
                                                'Uploading Files'}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {progress.current} of {progress.total} files
                                </span>
                            </div>
                            {progress.currentFile && progress.status === 'uploading' && (
                                <div className="text-xs text-gray-500 truncate max-w-xs">
                                    {progress.currentFile}
                                </div>
                            )}
                        </div>
                    </div>

                    {progress.status === 'uploading' && (
                        <div className="hidden sm:block">
                            <span className="text-sm font-mono text-gray-600">
                                {Math.round(progress.overall)}%
                            </span>
                        </div>
                    )}

                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                        title="Close"
                    >
                        <X className="h-4 w-4 text-gray-500" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;